import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Grid,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { repairsApi, Repair } from '../services/repairsApi';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const Repairs: React.FC = () => {
    const { t } = useLanguage();
    
    const [repairs, setRepairs] = useState<Repair[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingRepair, setEditingRepair] = useState<Repair | null>(null);
    const formatDateForInput = (d: any) => {
        if (!d) return '';
        const date = new Date(d);
        return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
    };

    const [formData, setFormData] = useState<Partial<Repair>>({
        vehIdf: 0,
        rprDate: new Date(),
        rprGarage: '',
        rprDescription: '',
        rprCost: 0,
        rprStatus: 'planned'
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const [repairsRes, vehiclesRes] = await Promise.all([
                repairsApi.getRepairs(),
                vehiclesApi.getAll()
            ]);

            if (vehiclesRes.success) {
                setVehicles(vehiclesRes.data);
            }

            setRepairs(repairsRes);
        } catch (error) {
            console.error('Failed to load data:', error);
            blobticMessage.error(t('common.messages.error'), 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleOpenDialog = (repair?: Repair) => {
        if (repair) {
            setEditingRepair(repair);
            setFormData({
                ...repair
            });
        } else {
            setEditingRepair(null);
            setFormData({
                vehIdf: vehicles.length > 0 ? vehicles[0].id : 0,
                rprDate: new Date(),
                rprGarage: '',
                rprDescription: '',
                rprCost: 0,
                rprStatus: 'planned'
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            let response;
            if (editingRepair) {
                response = await repairsApi.updateRepair(editingRepair.id, formData);
            } else {
                response = await repairsApi.createRepair(formData);
            }

            blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
            setIsDialogOpen(false);
            loadData();
        } catch (error) {
            console.error('Error saving repair:', error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.error'));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.actions.deleteConfirm'))) return;
        try {
            await repairsApi.deleteRepair(id);
            blobticMessage.success(t('common.messages.success'), t('common.messages.deleted'));
            loadData();
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Unknown error');
        }
    };

    const columns = [
        { id: 'vehRegistration', label: t('parc.repairs.vehicle'), minWidth: 120 },
        { id: 'rprDate', label: t('parc.repairs.date'), minWidth: 100, format: (value: Date | string) => value ? new Date(value).toLocaleDateString() : '' },
        { id: 'rprGarage', label: t('parc.repairs.garage'), minWidth: 120 },
        { id: 'rprCost', label: t('parc.repairs.cost'), minWidth: 80 },
        {
            id: 'rprStatus',
            label: t('parc.repairs.status'),
            minWidth: 100,
            format: (value: string) => {
                if (value === 'planned') return t('parc.repairs.statusPlanned');
                if (value === 'in_progress') return t('parc.repairs.statusInProgress');
                if (value === 'completed') return t('parc.repairs.statusCompleted');
                if (value === 'cancelled') return t('parc.repairs.statusCancelled');
                return value;
            }
        }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.repairs.newRepair')}
        </Button>
    );

    return (
        <GenericPage title={t('parc.repairs.title')} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={repairs}
                onEdit={handleOpenDialog}
                onDelete={(r: any) => handleDelete(r.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingRepair ? t('parc.repairs.editRepair') : t('parc.repairs.newRepair')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>{t('parc.repairs.vehicle')}</InputLabel>
                                <Select
                                    value={formData.vehIdf || ''}
                                    label={t('parc.repairs.vehicle')}
                                    onChange={(e) => setFormData({ ...formData, vehIdf: Number(e.target.value) })}
                                >
                                    {vehicles.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.registrationNumber} - {v.brand}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.repairs.status')}</InputLabel>
                                <Select
                                    value={formData.rprStatus || 'planned'}
                                    label={t('parc.repairs.status')}
                                    onChange={(e) => setFormData({ ...formData, rprStatus: e.target.value })}
                                >
                                    <MenuItem value="planned">{t('parc.repairs.statusPlanned')}</MenuItem>
                                    <MenuItem value="in_progress">{t('parc.repairs.statusInProgress')}</MenuItem>
                                    <MenuItem value="completed">{t('parc.repairs.statusCompleted')}</MenuItem>
                                    <MenuItem value="cancelled">{t('parc.repairs.statusCancelled')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label={t('parc.repairs.date')}
                                InputLabelProps={{ shrink: true }}
                                value={formatDateForInput(formData.rprDate)}
                                onChange={(e) => setFormData({ ...formData, rprDate: new Date(e.target.value) })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('parc.repairs.garage')}
                                value={formData.rprGarage || ''}
                                onChange={(e) => setFormData({ ...formData, rprGarage: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.repairs.cost')}
                                value={formData.rprCost || 0}
                                onChange={(e) => setFormData({ ...formData, rprCost: Number(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label={t('parc.repairs.description')}
                                value={formData.rprDescription || ''}
                                onChange={(e) => setFormData({ ...formData, rprDescription: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default Repairs;
